import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { gql, useQuery } from '@apollo/client'

import FetchMore from 'js/components/FetchMore'
import LoadingSpinner from 'js/components/LoadingSpinner/LoadingSpinner'
import SearchBar from 'js/components/SearchBar/SearchBar'
import { useIsMount } from 'js/lib/helpers'

import FollowerItem from './FollowerItem'

interface Props {
    guid: string
    selected?: Map<string, string>
    onSelect?: (guid: string) => void
}

const FollowersListSearch = ({ guid, selected, onSelect }: Props) => {
    const [searchInput, setSearchInput] = useState('')
    const onChangeSearchInput = (evt: React.ChangeEvent<HTMLInputElement>) => {
        setSearchInput(evt.target.value)
    }

    const [q, setQ] = useState('')
    const onSubmitSearchInput = (evt: React.FormEvent<HTMLFormElement>) => {
        evt.preventDefault()
        setQ(searchInput)
    }

    const [queryLimit, setQueryLimit] = useState(50)
    const { loading, data, fetchMore, refetch } = useQuery(GET_FOLLOWERS_LIST, {
        variables: {
            guid: guid,
            query: q,
            offset: 0,
            limit: queryLimit,
        },
    })

    const isMount = useIsMount()

    useEffect(() => {
        if (isMount) {
            refetch()
        }
    }, [isMount, refetch, guid])

    const { t } = useTranslation()

    return (
        <>
            <form method="GET" onSubmit={onSubmitSearchInput}>
                <SearchBar
                    name="send-message-followers-search"
                    value={searchInput}
                    label={t('followers.search-placeholder')}
                    onChange={onChangeSearchInput}
                    size="large"
                />
            </form>

            {loading ? (
                <LoadingSpinner />
            ) : data.entity?.followers ? (
                // @ts-ignore
                <FetchMore
                    edges={data?.entity?.followers?.results || []}
                    getMoreResults={(data: any) =>
                        data.entity.followers.results
                    }
                    fetchMore={fetchMore}
                    fetchType="scroll"
                    fetchCount={50}
                    setLimit={setQueryLimit}
                    maxLimit={data?.entity?.followers?.total}
                    isScrollableBox
                >
                    <div style={{ padding: '8px 0' }}>
                        {(data?.entity?.followers?.results || []).map(
                            (entity: any) => (
                                <FollowerItem
                                    key={entity.guid}
                                    user={entity}
                                    onSelect={onSelect}
                                    selected={selected}
                                />
                            ),
                        )}
                    </div>
                </FetchMore>
            ) : null}
        </>
    )
}

const followersFragment = `
    followers(limit: $limit, q: $query) {
        total
        results {
            guid
            name
            icon
            url
        }
    }
`

const GET_FOLLOWERS_LIST = gql`
    query FollowersList($guid: String!, $limit: Int!, $query: String) {
        entity(guid: $guid) {
            guid
            ... on Blog {
                ${followersFragment}
            }
            ... on News {
                ${followersFragment}
            }
            ... on Question {
                ${followersFragment}
            }
            ... on Discussion {
                ${followersFragment}
            }
            ... on Podcast {
                ${followersFragment}
            }
            ... on GenericArticle {
                ${followersFragment}
            }
        }
    }
`

export default FollowersListSearch
